'use client';

import {
  GTMEvent,
  Product,
  GTMUserType,
  GA4EventParams,
  BreadcrumbResultType
} from '@akinon/next/types';
import dayjs from 'dayjs';

let isPageViewPushed = false;
const eventQueue = [];

//In GA4 this is automatically collected. No need to push it manually for GA4.
export const pushPageView = ({ url }: { url: string }) => {
  pushEventGA4('page_view', {
    page_location: url
  });
};

export const pushAddToCart = (
  product: Product,
  currency?: string,
  quantity?: number,
  breadcrumb?: BreadcrumbResultType[]
) => {
  pushEventGA4('add_to_cart', {
    ecommerce: {
      currency:
        product?.currency_type?.toUpperCase() || currency?.toUpperCase() || '',
      value: product?.price || null,
      items: createGA4Items(product, { quantity, breadcrumb })
    }
  });
};

export const pushRemoveFromCart = (product: Product, quantity?: number) => {
  pushEventGA4('remove_from_cart', {
    ecommerce: {
      currency: product?.currency_type?.toUpperCase() || '',
      value: product?.price || null,
      items: createGA4Items(product, { quantity })
    }
  });
};

export const pushCartView = (products: any[], totalAmount: number) => {
  pushEventGA4('view_cart', {
    ecommerce: {
      currency: products[0]?.currency_type.toUpperCase() || '',
      value: totalAmount || null,
      coupon: products[0]?.coupon || '',
      items: createGA4Items(products)
    }
  });
};

export const pushBeginCheckout = (
  products: Product[],
  totalAmount?: number
) => {
  const productCurrency = products[0]?.currency_type?.toUpperCase() || '';

  pushEventGA4('begin_checkout', {
    ecommerce: {
      currency: productCurrency,
      value: totalAmount || null,
      coupon: products[0]?.coupon || '',
      items: createGA4Items(products)
    }
  });
};

export const pushAddShippingInfo = (
  products: Product[],
  totalAmount: number,
  shippingType: string,
  shippingAmount?: number
) => {
  pushEventGA4('add_shipping_info', {
    ecommerce: {
      currency: products[0]?.currency_type?.toUpperCase() || '',
      value: totalAmount || null,
      shipping_tier: shippingType,
      shipping: shippingAmount || 0,
      items: createGA4Items(products)
    }
  });
};

export const pushAddPaymentInfo = (
  products: Product[],
  payment_type: string,
  shippingType: string,
  totalAmount?: number
) => {
  const productCurrency = products[0]?.currency_type?.toUpperCase() || '';

  pushEventGA4('add_payment_info', {
    ecommerce: {
      currency: productCurrency || '',
      shipping_tier: shippingType || '',
      value: totalAmount || null,
      payment_type: payment_type || '',
      coupon: products[0]?.coupon || '',
      items: createGA4Items(products)
    }
  });
};

export const pushProductListProductViewed = (
  products: Product[],
  currentPage: number = 1,
  pageSize: number
) => {
  const { item_list_id, item_list_name } = getGA4ListInfo();

  pushEventGA4('view_item_list', {
    ecommerce: {
      item_list_id,
      item_list_name,
      items: createGA4Items(products, {
        page: currentPage,
        pageSize
      })
    }
  });
};

export const pushProductClicked = (product: Product, index: number) => {
  const { item_list_id, item_list_name } = getGA4ListInfo();

  addProductToLocalStorage(product?.sku, item_list_id, item_list_name, index);

  pushEventGA4('select_item', {
    ecommerce: {
      item_list_id,
      item_list_name,
      items: createGA4Items(product, { productIndex: index })
    }
  });
};

export const pushProductViewed = (
  product: Product,
  breadcrumb?: BreadcrumbResultType[]
) => {
  pushEventGA4('view_item', {
    ecommerce: {
      currency: product?.currency_type?.toUpperCase() || '',
      value: product?.price || null,
      items: createGA4Items(product, { breadcrumb })
    }
  });
};

export const pushSearchViewed = (category: string, searchTerm: string) => {
  pushEventGA4('view_search_result', {
    search_page_category: category || '',
    search_term: searchTerm || ''
  });
};

export const pushAddToWishlist = (product: Product) => {
  pushEventGA4('add_to_wishlist', {
    ecommerce: {
      items: createGA4Items(product)
    }
  });
};

const generatePromotionPayload = (promotion) => {
  // If the payload already contains ecommerce structure, use it directly
  if (promotion?.ecommerce) {
    return promotion;
  }

  // Otherwise, use the old format
  return {
    creative_name: promotion?.creative_name,
    promotion_id: promotion?.promotion_id,
    promotion_name: promotion?.promotion_name,
    items: promotion?.items?.map((item) => ({
      item_id: item?.item_id?.toString(),
      item_name: item?.item_name,
      price: item?.price,
      quantity: item?.quantity
    }))
  };
};

export const pushSelectPromotion = (promotion) => {
  const payload = generatePromotionPayload(promotion);
  pushEventGA4('select_promotion', payload);
};

export const pushViewPromotion = (promotion) => {
  const payload = generatePromotionPayload(promotion);
  pushEventGA4('view_promotion', payload);
};

export const pushSignUp = async (email?: string) => {
  const hashedEmail = await hashEmail(email || '');

  pushEventGA4('sign_up', {
    method: 'Eposta',
    email: hashedEmail
  });
};

export const pushLogin = async (email?: string) => {
  const hashedEmail = await hashEmail(email || '');

  pushEventGA4('login', {
    method: 'Eposta',
    email: hashedEmail
  });
};

const hashEmail = async (email: string): Promise<string> => {
  if (!email) return '';

  const encoder = new TextEncoder();
  const data = encoder.encode(email);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return hashHex;
};

export const pushPurchaseEvent = async (order) => {
  const transactionId = order?.number?.toString();
  const currency = order?.currency?.value?.toUpperCase();
  const totalAmount = Number(order?.amount);

  const products: Product[] = order.orderitem_set.map((item) => ({
    ...item.product,
    price: item.price || item.product?.price || null,
    retail_price: item.retail_price || item.product?.retail_price || null,
    quantity: item.quantity || 1
  }));

  const hashedEmail = await hashEmail(order?.user_email || '');

  // Extract coupon code from discountitem_set
  const couponCode =
    order?.discountitem_set?.find((item) => item.code)?.code || '';

  // Calculate discount rate
  const discountAmount = Number(order?.discount_amount) || 0;
  const amountWithoutDiscount = Number(order?.amount_without_discount) || 0;
  const discountRate =
    discountAmount && amountWithoutDiscount
      ? Number((discountAmount / amountWithoutDiscount) * 100).toFixed(2)
      : 0;

  pushEventGA4('purchase', {
    ecommerce: {
      transaction_id: transactionId,
      currency,
      value: totalAmount,
      email: hashedEmail,
      coupon: couponCode,
      discount: discountAmount,
      discount_rate: discountRate,
      paeyment_type: order?.payment_option?.name || '',
      shipping_tier: order?.shipping_option_slug || '',
      tax: order?.tax || null,
      shipping: order?.shipping_amount || 0,
      items: createGA4Items(products)
    }
  });
};

export const pushFilterInteraction = ({
  action,
  category,
  value,
  activeFilters
}: {
  action: 'select' | 'deselect';
  category: string;
  value: string;
  activeFilters: string[];
}) => {
  pushEventGA4('filter_interaction', {
    filter_action: action,
    filter_category: category || '',
    filter_value: value || '',
    active_filter_count: activeFilters.length,
    active_filters: activeFilters.join(', ')
  });
};

export const pushCouponApplied = (coupon) => {
  pushEventGA4('code_applied', {
    coupon_code: coupon || ''
  });
};

export const pushEvent = (event: GTMEvent) => {
  const { Category = 'Enhanced Ecommerce', Value = 0, ...rest } = event;

  window.dataLayer.push({ Category, Value, ...rest });
};

export const pushEventGA4 = (
  eventAction: string,
  eventParams?: GA4EventParams
) => {
  if (!isPageViewPushed && eventAction !== 'page_view') {
    eventQueue.push({ eventAction, eventParams });
    return;
  }

  if (eventAction === 'page_view') {
    isPageViewPushed = true;

    window.dataLayer.push({
      event: eventAction,
      ...eventParams
    });

    while (eventQueue.length > 0) {
      const queued = eventQueue.shift();
      window.dataLayer.push({
        event: queued.eventAction,
        ...queued.eventParams
      });
    }

    return;
  }

  window.dataLayer.push({
    event: eventAction,
    ...eventParams
  });
};

const getGA4ListInfo = () => {
  const productListInfo = JSON.parse(localStorage.getItem('ga4ListInfo')) || [];
  const item_list_id =
    productListInfo?.item_list_id || window.location.pathname || '';
  const item_list_name =
    productListInfo?.item_list_name || window.location.pathname || '';
  return { item_list_id, item_list_name };
};

const addProductToLocalStorage = (sku, itemListId, itemListName, index) => {
  const infos = JSON.parse(localStorage.getItem('ga4ProductInfos')) || [];

  const updatedInfos = infos.filter((item) => item.product_sku !== sku);

  updatedInfos.push({
    product_sku: sku,
    item_list_id: itemListId,
    item_list_name: itemListName,
    index
  });

  if (updatedInfos.length > 100) updatedInfos.shift();

  localStorage.setItem('ga4ProductInfos', JSON.stringify(updatedInfos));
};

const getProductFromLocalStorage = (sku) => {
  const infos = JSON.parse(localStorage.getItem('ga4ProductInfos')) || [];
  return infos.find((item) => item.product_sku == sku) || null;
};

export const priceDiscountPercentage = (retailPrice: string, price: string) => {
  price = Number(price).toFixed();
  retailPrice = Number(retailPrice).toFixed();
  const priceDiff = Number(retailPrice) - Number(price);
  return ((priceDiff / Number(retailPrice)) * 100).toFixed();
};

export const priceDiscountAmount = (retailPrice: string, price: string) => {
  price = Number(price).toFixed(2);
  retailPrice = Number(retailPrice).toFixed(2);
  return (Number(retailPrice) - Number(price)).toFixed(2);
};

export const createGA4Items = (
  products: Product | Product[],
  options?: {
    productIndex?: number;
    page?: number;
    pageSize?: number;
    quantity?: number;
    breadcrumb?: BreadcrumbResultType[];
  }
) => {
  const productList = Array.isArray(products) ? products : [products];
  if (productList.length === 0) return [];

  const { page, pageSize, quantity, productIndex, breadcrumb } = options || {};

  const filteredBreadcrumb = breadcrumb?.filter(
    (b) => b?.extra_context?.attributes?.category_id !== null
  );

  const { item_list_id, item_list_name } = getGA4ListInfo();

  return productList.map((product, i) => {
    const localStorageItem = getProductFromLocalStorage(product?.sku);
    const storedIndex = localStorageItem?.index;
    const resolvedIndex =
      storedIndex ??
      productIndex ??
      (page && pageSize ? (page - 1) * pageSize + i + 1 : 1);

    return {
      item_id: product?.sku?.toString() || '',
      item_name: product?.name || '',
      index: resolvedIndex,
      item_brand: 'Yatsan',
      item_category:
        filteredBreadcrumb?.[0]?.label || product?.attributes?.group || '',
      item_category2:
        filteredBreadcrumb?.[1]?.label || product?.attributes?.brand || '',
      item_category3:
        filteredBreadcrumb?.[2]?.label || product?.attributes?.category_3 || '',
      item_category4:
        filteredBreadcrumb?.[3]?.label || product?.attributes?.category_4 || '',
      item_category5:
        filteredBreadcrumb?.[4]?.label || product?.attributes?.category_5 || '',
      item_list_id,
      item_list_name,
      item_variant:
        ((product?.attributes?.erp_size_variant ||
          product?.attributes?.size_variant) &&
          `${
            product?.attributes?.erp_size_variant ||
            product?.attributes?.size_variant
          } cm`) ||
        null,

      first_price: Number(product?.retail_price) || null,
      discount: Number(
        priceDiscountAmount(product?.retail_price, product?.price)
      ),
      price: Number(product?.price) || null,
      discount_rate: Number(
        priceDiscountPercentage(product?.retail_price, product?.price)
      ),
      quantity: quantity || product?.quantity || 1
    };
  });
};
